<template>
  <label
    :class="[
      'cms-radio-button',
      {
        'is-active': isChecked,
        'is-disabled': isDisabled,
        [`cms-radio-button--${radioSize}`]: radioSize
      }
    ]"
  >
    <input
      ref="radioRef"
      v-model="model"
      class="cms-radio-button__original"
      type="radio"
      :name="name || radioGroup?.name"
      :disabled="isDisabled"
      :value="label"
      @change="handleChange"
    >
    <span
      class="cms-radio-button__inner"
      :style="activeStyle"
    >
      <slot>{{ label }}</slot>
    </span>
  </label>
</template>

<script lang="ts" setup>
import { ref, computed, inject } from 'vue'
import { radioButtonProps, radioButtonEmits } from './radio-button'
import { radioGroupKey } from './radio'

defineOptions({
  name: 'CmsRadioButton'
})

const props = defineProps(radioButtonProps)
const emit = defineEmits(radioButtonEmits)

// 注入 RadioGroup 上下文
const radioGroup = inject(radioGroupKey, undefined)

// 单选框引用
const radioRef = ref<HTMLInputElement | null>(null)

// 计算尺寸
const radioSize = computed(() => props.size || radioGroup?.size.value || '')

// 计算是否禁用
const isDisabled = computed(() => props.disabled || radioGroup?.disabled.value || false)

// 计算是否选中
const isChecked = computed(() => model.value === props.label)

// 计算绑定值
const model = computed({
  get() {
    return radioGroup ? radioGroup.modelValue.value : props.modelValue
  },
  set(val: string | number | boolean) {
    if (radioGroup) {
      radioGroup.onChange(val)
    } else {
      emit('update:modelValue', val)
    }
    radioRef.value && (radioRef.value.checked = isChecked.value)
  }
})

// 计算激活样式
const activeStyle = computed(() => {
  if (!radioGroup) return {}
  return {
    backgroundColor: isChecked.value ? radioGroup.fill : '',
    borderColor: isChecked.value ? radioGroup.fill : '',
    boxShadow: isChecked.value ? `-1px 0 0 0 ${radioGroup.fill}` : '',
    color: isChecked.value ? radioGroup.textColor : ''
  }
})

// 处理变更事件
const handleChange = () => {
  emit('change', model.value)
}
</script>

<style>
.cms-radio-button {
  position: relative;
  display: inline-block;
  outline: none;
}

.cms-radio-button__inner {
  position: relative;
  display: inline-block;
  padding: 8px 15px;
  font-size: 14px;
  line-height: 1;
  white-space: nowrap;
  border: 1px solid #dcdfe6;
  border-left: 0;
  color: #606266;
  background: #fff;
  cursor: pointer;
  transition: all .3s cubic-bezier(.645,.045,.355,1);
  user-select: none;
}

.cms-radio-button__original {
  opacity: 0;
  outline: none;
  position: absolute;
  z-index: -1;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  margin: 0;
}

/* 尺寸 */
.cms-radio-button--large .cms-radio-button__inner {
  padding: 12px 19px;
  font-size: 14px;
}

.cms-radio-button--small .cms-radio-button__inner {
  padding: 5px 11px;
  font-size: 12px;
}

/* 状态 */
.cms-radio-button.is-active .cms-radio-button__inner {
  color: #fff;
  background-color: #409eff;
  border-color: #409eff;
  box-shadow: -1px 0 0 0 #409eff;
}

.cms-radio-button.is-disabled {
  cursor: not-allowed;
}

.cms-radio-button.is-disabled .cms-radio-button__inner {
  color: #c0c4cc;
  cursor: not-allowed;
  background-image: none;
  background-color: #fff;
  border-color: #ebeef5;
  box-shadow: none;
}

.cms-radio-button.is-disabled.is-active .cms-radio-button__inner {
  color: #fff;
  background-color: #f2f6fc;
}

/* 悬停效果 */
.cms-radio-button:not(.is-disabled):hover .cms-radio-button__inner {
  color: #409eff;
}

.cms-radio-button:not(.is-disabled).is-active:hover .cms-radio-button__inner {
  color: #fff;
}
</style>